import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Input, Button, List, Checkbox, Pagination, Spin, Empty, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { productAPI } from '../api';
import { normalizeMoney } from './money';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg width="72" height="72" xmlns="http://www.w3.org/2000/svg"><rect width="72" height="72" fill="#f0f0f0"/><text x="50%" y="50%" fill="#999999" font-size="12" text-anchor="middle" dominant-baseline="middle">No Image</text></svg>'
)}`;

const extractVariantPrices = (variants) => {
  if (!variants) return [];

  const prices = [];

  const collectFromValue = (value) => {
    if (!value) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(collectFromValue);
      return;
    }

    if (typeof value === 'object') {
      if (value.SKU !== undefined || value.variant_path) {
        if (value.price !== undefined) {
          prices.push({
            price: Number(value.price) || 0,
            currency: value.currency || 'USD',
          });
        }
      }

      Object.values(value).forEach(collectFromValue);
    }
  };

  collectFromValue(variants);
  return prices;
};

const buildCoverUrl = (product) => {
  if (!product?.images?.length) {
    return PLACEHOLDER_IMAGE;
  }

  const coverImage = product.images.find((img) => !img.for_variants) || product.images[0];
  if (!coverImage) {
    return PLACEHOLDER_IMAGE;
  }

  return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
};

const formatPriceLabel = (product) => {
  const variantPrices = extractVariantPrices(product?.variants);
  if (!variantPrices.length) {
    return 'No price data';
  }

  const lowestPrice = variantPrices.reduce(
    (acc, current) => (current.price < acc.price ? current : acc),
    variantPrices[0],
  );

  try {
    const normalized = normalizeMoney(lowestPrice.price);
    return `${lowestPrice.currency || 'USD'} ${normalized}`;
  } catch (error) {
    return `${lowestPrice.currency || 'USD'} ${lowestPrice.price}`;
  }
};

const ProductSearchSelector = ({
  multiple = false,
  pageSize = 10,
  defaultSelectedIds = [],
  onConfirm,
  onSelect,
  onSelectionChange,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });
  const [selectedIds, setSelectedIds] = useState(() => new Set(defaultSelectedIds));

  const keywordRef = useRef('');
  const onSelectionChangeRef = useRef(onSelectionChange);
  const isInitialMount = useRef(true);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    // 只在初始挂载时设置默认选中的 ID
    if (isInitialMount.current) {
      setSelectedIds(new Set(defaultSelectedIds || []));
      isInitialMount.current = false;
    }
  }, [defaultSelectedIds]);

  const fetchProducts = useCallback(
    async (page = 1, overrideKeyword) => {
      const keywordToUse =
        overrideKeyword !== undefined ? overrideKeyword : keywordRef.current || '';
      keywordRef.current = keywordToUse;

      const params = {
        page,
        page_size: pageSize,
      };

      if (keywordToUse.trim()) {
        params.search = keywordToUse.trim();
      }

      try {
        setLoading(true);
        const response = await productAPI.getAll(params);
        const payload = response.data || {};
        setResults(payload.data || []);
        setPagination({
          current: payload.page || page,
          total: payload.total || 0,
        });
      } catch (error) {
        
        message.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    fetchProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (value) => {
    const trimmed = (value || '').trim();
    setInputValue(trimmed);
    fetchProducts(1, trimmed);
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handlePageChange = (page) => {
    fetchProducts(page);
  };

  const toggleSelection = useCallback((productId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  }, []);

  const handleConfirm = () => {
    if (!onConfirm) {
      return;
    }
    onConfirm(Array.from(selectedIds));
  };

  const renderListHeader = useMemo(() => {
    const baseStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      fontWeight: 500,
      color: '#555',
      borderBottom: '1px solid #f0f0f0',
    };

    return (
      <div style={baseStyle}>
        <div style={{ width: multiple ? 48 : 88 }} />
        <div style={{ flex: 1 }}>Product</div>
        <div style={{ width: 140, textAlign: 'right' }}>Lowest Price</div>
      </div>
    );
  }, [multiple]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input
          placeholder="Enter keyword"
          value={inputValue}
          onChange={handleInputChange}
          onPressEnter={() => handleSearch(inputValue)}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={() => handleSearch(inputValue)}>
          Search
        </Button>
      </div>

      <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
        {renderListHeader}
        <div style={{ minHeight: 240 }}>
          <Spin spinning={loading}>
            {!loading && results.length === 0 ? (
              <Empty description="No products found" style={{ margin: '40px 0' }} />
            ) : (
              <List
                dataSource={results}
                renderItem={(product) => {
                  const coverUrl = buildCoverUrl(product);
                  const priceLabel = formatPriceLabel(product);

                  return (
                    <List.Item
                      key={product.id}
                      style={{ display: 'flex', alignItems: 'center', padding: '12px 16px' }}
                    >
                      <div style={{ width: multiple ? 48 : 88 }}>
                        {multiple ? (
                          <Checkbox
                            checked={selectedIds.has(product.id)}
                            onChange={(e) => toggleSelection(product.id, e.target.checked)}
                          />
                        ) : (
                          <Button
                            type="link"
                            onClick={() => {
                              if (onSelectionChange) {
                                onSelectionChange([product.id]);
                              }
                              if (onSelect) {
                                onSelect(product.id, product);
                              }
                            }}
                          >
                            Select
                          </Button>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 16 }}>
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#fafafa',
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={coverUrl}
                            alt={product.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(event) => {
                              event.currentTarget.src = PLACEHOLDER_IMAGE;
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>{product.name}</div>
                          <div style={{ color: '#888', fontSize: 12 }}>{product.id}</div>
                        </div>
                      </div>

                      <div style={{ width: 140, textAlign: 'right', fontWeight: 500 }}>
                        {priceLabel}
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </Spin>
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Pagination
          current={pagination.current}
          total={pagination.total}
          pageSize={pageSize}
          onChange={handlePageChange}
          size="small"
          showSizeChanger={false}
        />
        {multiple && (
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
          >
            Confirm
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductSearchSelector;
